/**
 * REF Phase 2 (v2) — NGXS action set mirroring {@code TsmAppState}'s edit-form
 * lifecycle (BootstrapForm / ApplyDeltas / OnAdaptorChange[Batch] / SaveForm /
 * CloseForm) plus REF-specific {@code PickRefRecord} for the self-lookup
 * round-trip.
 *
 * Action discriminators are {@code [RefApp] <Name>} so devtools and the action
 * stream stay distinct from {@code [TsmApp]} actions.
 *
 * Each class is a plain action carrier — reducers in {@link ref-app.state}
 * own the HTTP / state-patching logic. See tms-ref-apps.md §5.4.
 */

import { RefAppSnapshot } from '../../../../models/app-state-snapshot';
import { DeltaResponse, LookupMatch, TsmMode } from '../../models/ref-app.model';

export class InitRefAppState {
	static readonly type: string = '[RefApp] InitRefAppState';
	constructor(public readonly appName: string) {}
}

export class ClearRefAppState {
	static readonly type: string = '[RefApp] ClearRefAppState';
}

/**
 * Bootstrap the REF form. POSTs {@code ?action=update} with no formId; the
 * server mints a fresh formId and returns the full panes/fields/actions
 * snapshot via {@link ApplyRefDeltas}. Mirrors {@code BootstrapForm}.
 */
export class BootstrapRefForm {
	static readonly type: string = '[RefApp] BootstrapRefForm';
	constructor(
		public readonly mode: TsmMode,
		public readonly primaryKeys?: Record<string, string>,
		/**
		 * Pill identity override accompanying THESE primaryKeys — the state
		 * handler writes both atomically (SSOT), so no bootstrap path can
		 * leave a stale hint from a previous record or a previous REF app
		 * (RefAppState is one singleton slice shared by every REF page).
		 * Null = derive the pill from the primaryKeys join as usual. Callers
		 * loading a DIFFERENT record omit it; callers re-loading the SAME
		 * record (form-expiry retry, mode change, language re-bootstrap,
		 * snapshot restore) carry the current hint through.
		 */
		public readonly labelHint: string | null = null,
	) {}
}

/**
 * Merge a server response (bootstrap or field-update) into state. Mirrors
 * {@code ApplyDeltas}.
 */
export class ApplyRefDeltas {
	static readonly type: string = '[RefApp] ApplyRefDeltas';
	constructor(public readonly response: DeltaResponse) {}
}

/**
 * Single-field convenience wrapper around {@link OnRefAdaptorChangeBatch}.
 * REF has only the detail context (no filter pane), so no {@code context}
 * parameter is needed.
 */
export class OnRefAdaptorChange {
	static readonly type: string = '[RefApp] OnRefAdaptorChange';
	constructor(
		public readonly adaptor: string,
		public readonly value: string | number | boolean | null,
	) {}
}

/**
 * Multi-adaptor counterpart for range-pair edits that must travel as one
 * payload so the server's {@code to_*}-last sort preserves both halves.
 */
export class OnRefAdaptorChangeBatch {
	static readonly type: string = '[RefApp] OnRefAdaptorChangeBatch';
	constructor(public readonly fields: Record<string, string | number | boolean | null>) {}
}

/**
 * Fires the server-side save observer chain (legacy {@code fsc_submit}+1 path)
 * via {@code ?action=save}. Reads {@code formId} from current state.
 */
export class SaveRefForm {
	static readonly type: string = '[RefApp] SaveRefForm';
}

/**
 * Category A js-handler bridge — POSTs {@code ?action=app-command} with a
 * registered application subcommand (pane-button {@code server-command}
 * action, e.g. ATI_TENANT {@code generateClientId}) and applies the delta
 * response. Requires a live form (BootstrapRefForm first).
 */
export class RunRefAppCommand {
	static readonly type: string = '[RefApp] RunRefAppCommand';
	constructor(
		public readonly command: string,
		/** Optional flat string args merged into the subcommand's reqMap
		 *  server-side (Menu Entitlement toggles: FSCINDEX/VALUE). */
		public readonly args?: Readonly<Record<string, string>>,
	) {}
}

/**
 * Evicts the server-side FormCache entry via {@code ?action=close-form}.
 * Resets edit-form slices but KEEPS the per-app metadata (appName, appTitle,
 * selfLookupName, filterLookupFlag) so the page can re-bootstrap without
 * losing its chrome.
 */
export class CloseRefForm {
	static readonly type: string = '[RefApp] CloseRefForm';
}

/**
 * TMS Navigation — page-state preservation Path B (REF mirror of
 * {@code RebindForm}). Re-binds REF state to a still-cached
 * {@code BackEndAppProcessor} by formId. POSTs {@code ?action=rebind};
 * server emits the full bootstrap envelope read from the cached BAP, so
 * the user's in-flight typed values + matrix in-memory rows survive
 * verbatim without client-side replay.
 *
 * <p>{@code mode} and {@code primaryKeys} are carried through from the
 * snapshot so the handler can re-establish state identity slices wiped by
 * {@code ClearRefAppState}. The rebind response carries field deltas +
 * panes only — not the record's identity.
 *
 * <p>On 410 the orchestrator falls back to {@link BootstrapRefForm} with
 * the snapshot's PKs+mode.
 */
export class RebindRefForm {
	static readonly type: string = '[RefApp] RebindRefForm';
	constructor(
		public readonly formId: string,
		public readonly mode: TsmMode,
		public readonly primaryKeys: Record<string, string> | null,
		/** Same semantics as {@link BootstrapRefForm}'s labelHint — written
		 *  atomically with primaryKeys so restores can't resurrect a stale pill. */
		public readonly labelHint: string | null = null,
	) {}
}

/**
 * Self-lookup pick: builds the {@code primaryKeys} map from the picked row's
 * {@code values[]} keyed by the response's {@code primaryKeyFields[]}, then
 * dispatches {@link BootstrapRefForm} with those PKs. REF-specific; no TSM
 * equivalent (TSM picks records via the listing-row click path).
 */
export class PickRefRecord {
	static readonly type: string = '[RefApp] PickRefRecord';
	constructor(
		public readonly row: LookupMatch,
		public readonly primaryKeyFields: ReadonlyArray<string>,
		/**
		 * Pill identity override — non-null when the server's PK snapshot
		 * (minus tenant_cd) can't identify the record and the pill must show
		 * the picked row's display code instead of joining the load-key map.
		 * See RefAppStateModel.recordLabelHint.
		 */
		public readonly labelHint: string | null = null,
	) {}
}

/**
 * TMS Navigation — page-state preservation (Path B REF mirror). Restores
 * a REF page from a snapshot. REF has no listing and no Add Dialog so
 * the orchestrator only owns the detail-bootstrap pipeline: try
 * {@link RebindRefForm} first (cached BAP holds all backend data); on
 * 410 fall back to {@link BootstrapRefForm} from snapshot's PKs+mode;
 * finally emit via {@code coordinator.emit} so the detail component
 * runs its UI-restore callbacks (scrollTop).
 */
export class RestoreRefAppSnapshot {
	static readonly type: string = '[RefApp] RestoreRefAppSnapshot';
	constructor(public readonly snapshot: RefAppSnapshot) {}
}
